import { ComponentType } from "react";

export interface BroadcastFilter {
  bfc?: string; // selected consultant's user _id (empty/undefined = all SBCs)
  bfcName?: string; // display name for the selected consultant (review step only)
  city?: string[];
  zipcode?: string[];
  bussType?: string[];
  subBussType?: string[];
  classification?: string[];
  certification?: string[];
  businessChannels?: string[];
  emergOrEst?: string;
  lastContact?: string;
}

/** An ad-hoc address typed into the custom recipient list. */
export interface CustomRecipient {
  email: string;
  name?: string;
}

/** Which recipient sources a broadcast draws from. */
export interface RecipientSources {
  firms: boolean;
  custom: boolean;
}

/** Optional arithmetic returned by POST /broadcast/preview (absent on old servers). */
export interface PreviewBreakdown {
  firmRecipients: number;
  customValid: number;
  duplicates: number;
  suppressed: number;
  invalid: number;
  total: number;
}

/** Server rejects anything past this; we block before the request. */
export const MAX_CUSTOM_RECIPIENTS = 500;

export const defaultRecipientSources: RecipientSources = {
  firms: true,
  custom: false,
};

// Deliberately loose — this only screens out obvious typos, the server is the
// authority. No spaces/commas/semicolons/angle brackets, one @, a dotted TLD.
const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>.]+(\.[^\s@,;<>.]+)*\.[A-Za-z]{2,}$/;

export const isValidRecipientEmail = (email: string): boolean =>
  EMAIL_RE.test((email || "").trim());

/**
 * Splits a pasted/typed blob into candidate entries. Commas, semicolons and
 * newlines always separate; whitespace only separates tokens that aren't in
 * `Name <email@x.com>` form (so a display name survives the split).
 */
export const splitRecipientInput = (raw: string): string[] => {
  const tokens: string[] = [];
  let buf = "";
  let inAngle = false;
  let inQuote = false;

  for (const ch of raw || "") {
    if (ch === '"') {
      inQuote = !inQuote;
      buf += ch;
      continue;
    }
    if (!inQuote && ch === "<") {
      inAngle = true;
      buf += ch;
      continue;
    }
    if (!inQuote && ch === ">") {
      inAngle = false;
      buf += ch;
      continue;
    }
    if (!inAngle && !inQuote && (ch === "," || ch === ";" || ch === "\n" || ch === "\r" || ch === "\t")) {
      tokens.push(buf);
      buf = "";
      continue;
    }
    buf += ch;
  }
  tokens.push(buf);

  return tokens
    .map((t) => t.trim())
    .filter(Boolean)
    .reduce<string[]>((acc, token) => {
      // A token without angle brackets can still be several space-separated
      // bare addresses (the common spreadsheet / Outlook paste).
      if (!token.includes("<") && /\s/.test(token)) {
        return acc.concat(token.split(/\s+/).filter(Boolean));
      }
      acc.push(token);
      return acc;
    }, []);
};

/** Parses `Name <email@x.com>`, `"Name" <email@x.com>` and bare-email forms. */
export const parseCustomRecipient = (raw: string): CustomRecipient | null => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;

  const angled = trimmed.match(/^(.*?)<\s*([^<>]*?)\s*>$/);
  if (angled) {
    const name = angled[1].trim().replace(/^["']+|["']+$/g, "").trim();
    const email = angled[2].replace(/^mailto:/i, "").trim();
    if (!email) return null;
    return name ? { email, name } : { email };
  }

  return { email: trimmed.replace(/^mailto:/i, "") };
};

export interface BroadcastAttachment {
  name: string;
  storageName: string;
  path: string;
  download: string;
}

export interface ContentData {
  subject: string;
  htmlContent: string;
  eventId?: string;
  attachments?: BroadcastAttachment[];
}

export interface ContentStepProps {
  contentData: ContentData;
  setContentData: (data: ContentData) => void;
  onNext: () => void;
}

export interface BroadcastTypeConfig {
  id: string;
  label: string;
  description: string;
  iconName: string; // MUI icon name string — resolved in TypeSelector
}

export const BROADCAST_TYPES: BroadcastTypeConfig[] = [
  {
    id: "event_invitation",
    label: "Event Invitation",
    description:
      "Send an event invitation to firms. Choose an upcoming event and your recipients will receive a branded invite with event details.",
    iconName: "Event",
  },
  {
    id: "custom_email",
    label: "Custom Email",
    description:
      "Compose a custom broadcast email from scratch or from a saved template.",
    iconName: "Email",
  },
];
